import { inject, singleton } from 'tsyringe';
import { OWWindowsListenerDelegate } from '../../odk-ts';
import {
  kNameOfTesterWindowService,
  ITesterWindowService,
} from 'app-core/src/interfaces/tester-window-service.interface';
import { kTesterWindowName } from 'app-core/src/constants/constants';
import {
  ITesterWindowManagerService,
  ITesterWindowManagerServiceDelegate,
} from '../interfaces/tester-window-manager-service.interface';
import { RecommendationDatastoreService } from 'app-core/src/services/datastore-service/recommendation-datastore.service';
import { PersistentStorageService } from 'app-core/src/services/persistent-storage-service/persistent-storage.service';
import { Application } from 'app-core/src/models/application.model';
import { Game } from 'app-core/src/models/game.model';
import { kDITokenWindowListener } from 'app-core/src/constants/injectionTokens';
import { IOWWindowsListenerDelegateProvider } from 'app-core/src/interfaces/ow-wrappers/ow-window-listener-delegate-wrapper.interface';

// -----------------------------------------------------------------------------
@singleton()
export class TesterWindowService
  implements
    ITesterWindowService,
    ITesterWindowManagerService,
    OWWindowsListenerDelegate
{
  private readonly listeners: Set<ITesterWindowManagerServiceDelegate>;
  private isRunning: boolean;

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly persistentStorage: PersistentStorageService,
    private readonly datastoreService: RecommendationDatastoreService,
    @inject(kDITokenWindowListener)
    private readonly owWindowListener: IOWWindowsListenerDelegateProvider,
  ) {
    this.listeners = new Set<ITesterWindowManagerServiceDelegate>();
    this.isRunning = false;
    this.owWindowListener.addListener(this);
  }

  // ---------------------------------------------------------------------------
  private async triggerListenerEvent(
    game: Game,
    application: Application,
    useDevEnv: string,
  ) {
    if (!game || !application) {
      return;
    }

    for (const listener of this.listeners) {
      await listener.onTestingNotificationSet(game, application, useDevEnv);
    }
  }

  // ---------------------------------------------------------------------------
  IsNotificationRunning(): boolean {
    return this.isRunning;
  }

  // ---------------------------------------------------------------------------
  // TesterWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: ITesterWindowManagerServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: ITesterWindowManagerServiceDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // TesterWindowManagerService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  notificationAlreadyRunning(isRunning: boolean) {
    this.isRunning = isRunning;
  }

  // ---------------------------------------------------------------------------
  // TesterWindowService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  transferTesterNotification(
    game: Game,
    application: Application,
    useDevEnv: string = null,
  ) {
    this.triggerListenerEvent(game, application, useDevEnv);
  }

  // ---------------------------------------------------------------------------
  resetNotificationTimestamp() {
    this.persistentStorage.resetLastDisplayedNotificationTimestamp();
  }

  // ---------------------------------------------------------------------------
  resetLocalStorage() {
    this.persistentStorage.deleteLocalStorageItems();
  }

  // ---------------------------------------------------------------------------
  resetConditionsSummary() {
    this.persistentStorage.setConditionsSummary({
      applicationConditions: {},
      globalConditions: {},
      lastSummaryResetTimestamp: Date.now(),
    });
  }

  // ---------------------------------------------------------------------------
  resetNotificationsHistory() {
    this.persistentStorage.createNotificationHistory();
  }

  // ---------------------------------------------------------------------------
  getGlobalConfigurations() {
    return this.datastoreService.getGlobalConfigurations();
  }

  // ---------------------------------------------------------------------------
  getAllGamesConfigurations(): Promise<Game[]> {
    return this.datastoreService.getAllGames();
  }

  // ---------------------------------------------------------------------------
  getGameConfiguration(gameId: number) {
    return this.datastoreService.getGame(gameId);
  }

  // ---------------------------------------------------------------------------
  async setEnvironment(environment: string) {
    return this.datastoreService.setEnvironment(environment);
  }

  // ---------------------------------------------------------------------------
  // ExposableService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  get token(): string {
    return kNameOfTesterWindowService;
  }

  // ---------------------------------------------------------------------------
  // OWWindowsListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  onWindowClosed(windowName: string) {
    if (!windowName.endsWith(kTesterWindowName)) {
      return;
    }

    window.close();
  }
}
